import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { of } from 'rxjs';
import { map, mergeMap, catchError, withLatestFrom } from 'rxjs/operators';

import { ChatListService } from '../../shared/services/chat-list.service';

import * as ChatActions from './chat.actions';
import * as ChatSelectors from './chat.selectors';

import { IConversationData } from '../../interfaces/conversations.interfaces';

@Injectable()
export class ChatEffects {
  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.loadConversations),
      mergeMap(() =>
        this.chatListService.conversations$.pipe(
          map((conversations: IConversationData[]) =>
            ChatActions.loadConversationsSuccess({ conversations })
          ),
          catchError(error => of(ChatActions.loadConversationsFailure({ error: error.message })))
        )
      )
    )
  );

  addConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.addConversation),
      mergeMap(({ conversation }) => {
        this.chatListService.addConversation(conversation);
        return of(ChatActions.addConversationSuccess({ conversation }));
      }),
      catchError(error => of(ChatActions.addConversationFailure({ error: error.message })))
    )
  );

  updateConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.updateConversation),
      withLatestFrom(this.store.select(ChatSelectors.selectConversations)),
      mergeMap(([{ conversationId, updates }, conversations]) => {
        const conversation = conversations.find(conv => conv.conversation_id === conversationId);
        if (conversation) {
          const updatedConversation = { ...conversation, ...updates };
          this.chatListService.addConversation(updatedConversation);
          return of(ChatActions.updateConversationSuccess({ conversation: updatedConversation }));
        } else {
          return of(ChatActions.updateConversationFailure({ error: 'Conversation not found' }));
        }
      }),
      catchError(error => of(ChatActions.updateConversationFailure({ error: error.message })))
    )
  );

  updateLastMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.updateLastMessage),
      mergeMap(({ conversationId, message, creatorName }) => {
        this.chatListService.updateLastMessage(conversationId, message);
        return of(ChatActions.updateLastMessage({ conversationId, message, creatorName }));
      })
    )
  );

  toggleTypingIndicator$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatActions.toggleTypingIndicator),
      mergeMap(({ conversationId, isTyping }) => {
        this.chatListService.toggleTypingIndicator(conversationId, isTyping);
        return of(ChatActions.toggleTypingIndicator({ conversationId, isTyping }));
      })
    )
  );

  setConversations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ChatActions.setConversations),
        mergeMap(({ conversations }) => {
          this.chatListService.setConversations(conversations);
          return of();
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly chatListService: ChatListService
  ) {}
}
